import { ApplicationRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { BarcodeUtil } from 'depoto-core/src/utils'
import {
  Currency,
  Depot,
  File,
  Producer,
  Product,
  ProductDepot,
  ProductMove,
  Supplier,
  Vat,
} from 'depoto-core/src/entities'
import { FormRowTextareaComponent } from '../../_common/form-row-textarea/form-row-textarea.component'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { CoreService } from '../../../services'
import { SchemaUtil, getThumbUrl as imageGetThumbUrl } from '../../../utils'
import { ProductCreatingService } from '../../../services/product-creating.service'
import { TranslateService } from '@ngx-translate/core'

import * as Sentry from '@sentry/angular-ivy'
import { CompanyUtilService } from '../../../services/company-util.service'

declare let disableBarcodeScanner: boolean

export class ProductMoveForUnloadStock extends ProductDepot {
  amountToUnload: number
}

@Component({
  selector: 'modal-stock-operation-card',
  templateUrl: 'modal-stock-operation-card.component.html',
  styleUrls: ['modal-stock-operation-card.component.scss'],
})
export class ModalStockOperationCardComponent implements OnInit {
  @ViewChild('modal') public modal: ModalContainerComponent
  @ViewChild('inputNote') public inputNoteComponent: FormRowTextareaComponent
  @Input() public product: Product
  @Input() public type: string
  @Input() public depotFrom: number
  @Input() public depotTo: number
  @Input() public checkout: number
  @Input() public isCreatingProduct: boolean
  @Input() public isUpdatingProduct: boolean
  @Input() public alwaysLoadSelectData: boolean
  @Input() public hideButton: boolean
  @Input() public initDateDUZP: string
  @Input() public operationToUpdate: ProductMove
  @Input() public depots: Depot[] = []
  @Input() public suppliers: Supplier[] = []
  @Input() public producers: Producer[] = []
  @Input() public vats: Vat[] = []
  @Output() public result: EventEmitter<any> = new EventEmitter()
  @Input() moves: ProductMove[] = []
  @Input() depotQuantity: number
  selectedSupplier: number
  purchasePrice = 0
  currencies: Currency[]
  selectedCurrency: Currency
  dateDUZP: string
  sellPrice = ''
  quantity = 1
  inputQuantityMin = 1
  note = ''
  batch = ''
  expirationDate = ''
  position1 = ''
  position2 = ''
  position3 = ''
  resultMessage = ''
  isCreatedProduct = false
  isQuantityLocked = false
  loading = false
  keyEventSubscriptions: any = {}
  headerTitle: string
  public modalHeaderIcon: string
  public modalTitle: string
  public modalHeaderBgColorClass = ''
  selectCustomPositions = false
  productDepots: ProductMoveForUnloadStock[] = []
  @Input() productDepot: ProductDepot

  constructor(
    private appRef: ApplicationRef,
    private core: CoreService,
    private productCreatingService: ProductCreatingService,
    private translateService: TranslateService,
    public companyUtil: CompanyUtilService,
  ) {}

  ngOnInit() {
    this.getCurrencies()
    this.getSelectData()
    this.getModalData()
  }

  getCurrencies() {
    this.currencies = this.core.lists.currencies
    this.selectedCurrency = this.product?.purchaseCurrency
  }

  async getPackQuantity() {
    const result = await this.core.services.productPack.getList(
      { filters: { product: this.product.id } },
      SchemaUtil.productPack,
    )
    this.quantity = result.items[0]?.quantity ? result.items[0]?.quantity : 1
  }

  selectCustomPositionChange(event) {
    this.selectCustomPositions = event
    this.isQuantityLocked = !!event
    this.appRef.tick()
  }

  clearOptions() {
    this.isQuantityLocked = false
    this.selectCustomPositions = false
  }

  private getModalData() {
    if (this.isCreatedProduct) {
      if (this.type === 'in') {
        this.modalHeaderIcon = 'fa-truck'
        if (this.product) {
          this.modalTitle = this.translateService.instant('modal-stock-operation-card.load', {
            name: this.product.name,
          })
        }
      } else if (this.type === 'out') {
        this.modalHeaderIcon = 'fa-upload'
        if (this.product) {
          this.modalTitle = this.translateService.instant('modal-stock-operation-card.unload', {
            name: this.product.name,
          })
        }
      } else if (this.type === 'transfer') {
        this.modalHeaderIcon = 'fa-exchange'
        if (this.product) {
          this.modalTitle = this.translateService.instant('modal-stock-operation-card.transfer', {
            name: this.product.name,
          })
        }
      }
    } else {
      this.modalHeaderIcon = 'fa-file'
      this.isCreatingProduct ? (this.modalTitle = 'product.create') : (this.modalTitle = 'product.update')
    }
    if (!this.isUpdatingProduct) {
      this.type === 'out' ? (this.modalHeaderBgColorClass = 'bg-red') : (this.modalHeaderBgColorClass = 'bg-green')
    }
  }

  createProduct(): void {
    this.loading = true
    const tag = this.core.lists.tags.find(tag => tag.externalId === 'hs-count-by-piece')
    this.product.tags.push(tag)
    this.core.services.product
      .create(this.product, {
        id: null,
        tags: {
          id: null,
          externalId: null,
        },
      })
      .then(res => {
        this.loading = false
        if (res && res.id) {
          this.product = new Product(res)
          this.isCreatedProduct = true
          if (this.product.tags?.some(tag => tag?.externalId === 'hs-count-by-piece')) {
            this.productCreatingService.createOrUpdateProductPack(this.product)
          }
        } else {
          this.result.emit(res)
        }
        this.hideChildModal()
      })
  }

  getThumbUrl(image: File, format: 'w135wp' | 'w2048wp' = 'w135wp'): string {
    return imageGetThumbUrl(image, format)
  }

  updateProduct(): void {
    this.loading = true
    this.core.services.product
      .update(this.product)
      .then(res => {
        this.loading = false
        if (res && res.id) {
          this.product = new Product(res)
          this.result.emit(this.product)
        } else {
          this.result.emit(res)
        }
        this.hideChildModal()
      })
      .catch(err => this.result.emit(err))
  }

  createOperation(): void {
    if (this.type === 'in' && (!this.selectedSupplier || this.selectedSupplier < 1)) {
      alert(this.translateService.instant('modal-stock-operation-card.supplier.error'))
      return
    }
    if (isNaN(Number(this.quantity))) {
      alert(this.translateService.instant('modal-stock-operation-card.quantity.error'))
      return
    }
    if (
      this.type === 'out' &&
      Number(
        this.productDepots.reduce((currentTotal, pDepot) => {
          return Number(pDepot.amountToUnload) + Number(currentTotal)
        }, 0),
      ) !== Number(this.quantity)
    ) {
      alert(this.translateService.instant('modal-stock-operation-card.quantity-not-match'))
      return
    }
    let op: ProductMove
    switch (this.type) {
      case 'in':
        if (this.moves.some(move => move.product.id === this.product.id)) {
          this.moves = this.moves.filter(
            move =>
              move.product.id !== this.product.id ||
              move.batch !== this.batch ||
              move.expirationDate !== this.expirationDate ||
              move.position1 !== this.position1 ||
              move.position2 !== this.position2 ||
              move.position3 !== this.position3 ||
              Number(move.purchasePrice) !== Number(this.purchasePrice),
          )
        }
        op = new ProductMove({
          depotTo: this.depotTo,
          supplier: this.selectedSupplier,
          amount: Number(this.quantity),
          purchasePrice: Number(this.purchasePrice),
          purchasePriceSum: Number(this.purchasePrice) * Number(this.quantity),
          note: this.note,
          product: this.product,
          batch: this.batch,
          expirationDate: this.expirationDate,
          position1: this.position1,
          position2: this.position2,
          position3: this.position3,
          purchaseCurrency: this.selectedCurrency?.id,
        })
        this.moves.push(op)
        break
      case 'out':
        if (this.moves.some(move => move.product.id === this.product.id)) {
          this.moves = this.moves.filter(move => move.product.id !== this.product.id)
        }
        this.productDepots.forEach(pDepot => {
          if (pDepot.amountToUnload > 0) {
            op = new ProductMove({
              depotFrom: this.depotFrom,
              amount: Number(pDepot.amountToUnload),
              purchasePrice: Number(pDepot.purchasePrice),
              purchasePriceSum: Number(pDepot.purchasePrice) * Number(pDepot.amountToUnload),
              // purchasePrice: Number(this.purchasePrice), ?
              // purchasePriceSum: Number(this.purchasePrice) * Number(pDepot.amountToUnload),
              product: this.product,
              productDepot: pDepot.id,
              batch: pDepot.batch,
              expirationDate: pDepot.expirationDate,
              position1: pDepot.position1,
              position2: pDepot.position2,
              position3: pDepot.position3,
              purchaseCurrency: this.selectedCurrency?.id,
            })
            this.moves.push(op)
          }
        })
        break
      case 'transfer':
        if (
          this.moves.some(
            move =>
              move.product.id === this.product.id &&
              move.batch === this.productDepot.batch &&
              move.expirationDate === this.productDepot.expirationDate &&
              this.moves.some(move => move.productDepot === this.productDepot.id),
          )
        ) {
          this.moves = this.moves.filter(move => move.productDepot !== this.productDepot.id)
        }
        op = new ProductMove({
          depotFrom: this.depotFrom,
          productDepot: this.productDepot.id,
          depotTo: this.depotTo,
          amount: Number(this.quantity),
          note: this.note,
          product: this.product,
          supplier: this.selectedSupplier,
          batch: this.batch,
          expirationDate: this.expirationDate,
          position1: this.position1,
          position2: this.position2,
          position3: this.position3,
          purchaseCurrency: this.selectedCurrency?.id,
        })
        this.moves.push(op)
        break
    }
    if (this.operationToUpdate?.purchaseOrderItem?.id) {
      this.moves[0].purchaseOrderItem = this.operationToUpdate.purchaseOrderItem
    }
    this.result.emit(this.moves)
    window['selectedSupplier'] = this.selectedSupplier
    this.hideChildModal()
  }

  async fillNextEan() {
    this.product.ean = await this.core.services.product.getNextEan()
  }

  quantityChanged(event) {
    this.quantity = event
    let remainingQuantity = this.quantity
    this.productDepots.forEach((pDepot, index) => {
      if (remainingQuantity >= pDepot.quantityStock) {
        pDepot.amountToUnload = pDepot.quantityStock
        remainingQuantity -= pDepot.quantityStock
      } else {
        pDepot.amountToUnload = remainingQuantity
        remainingQuantity = 0
      }
    })
  }

  showProductOnChildModal(product?: Product, isCreatingProduct?: boolean, productEan?: string): void {
    this.loading = true
    this.isCreatingProduct = !!isCreatingProduct
    if (product && !isCreatingProduct) {
      this.product = product
    }
    this.showChildModal(productEan)
  }

  async getProductPack() {
    try {
      const result = await this.core.services.productPack.getList(
        { filters: { product: this.product.id } },
        SchemaUtil.productPack,
      )
      this.quantity = result.items[0]?.quantity ? result.items[0]?.quantity : 1
      // this.purchasePrice = Number((Number(this.product.purchasePrice) / Number(result.items[0]?.quantity)).toFixed(2))
      // nope. take purchasePrice for whole pack, whe we set quantity in pack..:
      this.purchasePrice = Number(this.product.purchasePrice)
    } catch (err) {
      console.log(err)
    } finally {
      this.loading = false
    }
  }

  public async showChildModal(productEan?: string) {
    this.loading = true
    this.selectedCurrency = this.product?.purchaseCurrency
    if (window['selectedSupplier'] && !this.selectedSupplier) {
      this.selectedSupplier = window['selectedSupplier']
    }
    if (this.product && this.product.supplier && this.product.supplier.id > 0 && !this.selectedSupplier) {
      this.selectedSupplier = this.product.supplier.id
    }
    if (!this.isCreatingProduct && !this.isUpdatingProduct) {
      this.keyEventSubscriptions.keyEnter = this.core.services.keyEvent.onKeyEnter.subscribe().then(() => {
        if (
          this.modal.isShown() &&
          this.inputNoteComponent &&
          this.inputNoteComponent.inputEl &&
          this.inputNoteComponent.inputEl.nativeElement &&
          this.inputNoteComponent.inputEl.nativeElement !== document.activeElement
        ) {
          // if textarea is not in focus
          this.createOperation()
        }
      })
      this.keyEventSubscriptions.keyEsc = this.core.services.keyEvent.onKeyEsc.subscribe().then(() => {
        this.hideChildModal()
      })
      this.keyEventSubscriptions.scannedPositions = this.core.services.keyEvent.onScannedPositions
        .subscribe()
        .then(positions => {
          this.position1 = positions[0]
          this.position2 = positions[1]
          this.position3 = positions[2]
          this.appRef.tick()
          setTimeout(() => this.createOperation())
        })
    }
    disableBarcodeScanner = true
    if (
      (this.type === 'in' && !this.depotTo) ||
      (this.type === 'out' && !this.depotFrom) ||
      (this.type === 'transfer' && (!this.depotFrom || !this.depotTo))
    ) {
      alert(this.translateService.instant('modal-stock-operation-card.select-warehouse'))
      disableBarcodeScanner = false
      return
    }
    this.productDepots = Object.assign([], this.getProductDepots())
    if (this.type === 'out' || this.type === 'transfer') {
      this.productDepots = this.productDepots.filter(pDepot => Number(pDepot.depot.id) === Number(this.depotFrom))
    }
    if (!this.isCreatingProduct && !this.isUpdatingProduct && this.product) {
      this.isCreatedProduct = true
    } else if (this.isCreatingProduct) {
      this.product = new Product({
        enabled: true,
        purchaseCurrency: this.core.services.user.user?.company?.currency,
        ...(this.core.services.user.user?.company?.batchExpirationMode === 'enabled-everywhere' && {
          enabledBatchExpiration: true,
        }),
      })
      this.isCreatedProduct = false
      if (productEan) {
        setTimeout(() => {
          this.product.ean = productEan
        }, 50)
      }
    } else if (this.isUpdatingProduct) {
      this.isCreatedProduct = false // todo: disputable, introduce new variable?
      if (productEan) {
        setTimeout(() => {
          this.product.ean = productEan
        }, 50)
      }
    }
    if (this.type === 'out') {
      if (this.moves.some(move => move.product.id === this.product.id)) {
        const pushedMovesWithSameProduct = this.moves.filter(move => move.product.id === this.product.id)
        this.quantity = 0
        this.productDepots.forEach(pDepot => {
          pushedMovesWithSameProduct.forEach(pushedMove => {
            if (pushedMove.productDepot === pDepot.id) {
              pDepot.amountToUnload = pushedMove.amount
              this.quantity = this.quantity + pDepot.amountToUnload
            }
          })
        })
      } else {
        this.quantity = 1
        this.productDepots.forEach(pDepot => {
          pDepot.amountToUnload = 0
        })
        this.productDepots[0].amountToUnload = 1
      }
    } else {
      this.quantity = 1
    }
    if (this.type === 'transfer') {
      this.selectedSupplier = this.productDepot.supplier.id
      this.expirationDate = this.productDepot.expirationDate
      this.batch = this.productDepot.batch
      this.quantity = this.productDepot.quantityStock
      if (this.quantity < 1) {
        this.inputQuantityMin = 0.01
      } else {
        this.inputQuantityMin = 1
      }
    }
    this.getViewSettings()
    this.fillLastUsedValues()
    this.getSelectData()
    this.getModalData()
    if (this.type === 'in' && this.product && this.product.tags.some(tag => tag.externalId === 'hs-count-by-piece')) {
      await this.getProductPack()
    } else this.loading = false
    if (this.operationToUpdate) {
      this.selectedSupplier = this.operationToUpdate.supplier
      this.purchasePrice = <any>this.operationToUpdate.purchasePrice
      this.sellPrice = <any>this.operationToUpdate.sellPrice
      this.quantity = <any>this.operationToUpdate.amount
      this.note = this.operationToUpdate.note
      this.batch = this.operationToUpdate.batch
      this.expirationDate = this.operationToUpdate.expirationDate
      this.position1 = this.operationToUpdate.position1
      this.position2 = this.operationToUpdate.position2
      this.position3 = this.operationToUpdate.position3
      if (this.operationToUpdate.purchaseCurrency) {
        this.selectedCurrency = this.currencies.find(c => c.id === this.operationToUpdate.purchaseCurrency)
      }
    }
    console.log(this.product)
    this.modal.showChildModal()
  }

  public hideChildModal(): void {
    this.clearOptions()
    disableBarcodeScanner = false
    if (this.keyEventSubscriptions.keyEnter) {
      this.keyEventSubscriptions.keyEnter.unsubscribe()
      this.keyEventSubscriptions.keyEnter = null
    }
    if (this.keyEventSubscriptions.keyEsc) {
      this.keyEventSubscriptions.keyEsc.unsubscribe()
      this.keyEventSubscriptions.keyEsc = null
    }
    if (this.keyEventSubscriptions.scannedPositions) {
      this.keyEventSubscriptions.scannedPositions.unsubscribe()
      this.keyEventSubscriptions.scannedPositions = null
    }
    this.selectedSupplier = null
    if (!this.isQuantityLocked) {
      this.quantity = 1
    }
    this.note = ''
    this.batch = ''
    this.expirationDate = ''
    this.position1 = ''
    this.position2 = ''
    this.position3 = ''
    this.modal.hideChildModal()
  }

  setViewSettings(): void {
    setTimeout(() => {
      this.core.services.storage.set(
        this.core.services.storage.keys.views.stockOperationCardSupplier,
        this.selectedSupplier,
      )
      this.core.services.storage.set(
        this.core.services.storage.keys.views.stockOperationCardIsLockedQuantity,
        this.isQuantityLocked,
      )
      this.core.services.storage.set(
        this.core.services.storage.keys.views.stockOperationCardLockedQuantity,
        this.quantity,
      )
    }, 100)
  }

  getViewSettings(): void {
    this.isQuantityLocked =
      this.core.services.storage.getSync(this.core.services.storage.keys.views.stockOperationCardIsLockedQuantity) ||
      false
    if (this.isQuantityLocked && !this.operationToUpdate) {
      this.quantity =
        this.core.services.storage.getSync(this.core.services.storage.keys.views.stockOperationCardLockedQuantity) || 1
    }
  }

  getSelectData() {
    if (this.product && !this.product.vat) {
      this.vats.forEach((vat: Vat) => {
        if (vat.default) {
          setTimeout(() => {
            this.product.vat = vat
            this.core.services.storage.set(this.core.services.storage.keys.views.stockOperationCardSelectData, {
              producers: this.producers,
              vats: this.vats,
              defaultVat: vat,
            })
          }, 500)
          return
        }
      })
    }
  }

  selectVat(event): void {
    this.vats.forEach((vat: Vat) => {
      if (vat.id === Number(event)) {
        this.product.vat = vat
        return
      }
    })
  }

  selectProducer(event): void {
    this.producers.forEach((producer: Producer) => {
      if (producer.id === Number(event)) {
        this.product.producer = producer
        return
      }
    })
  }

  setParent(parent: Product): void {
    this.product.parent = parent
    this.product.sellPrice = parent.sellPrice
    this.product.producer = parent.producer
    this.product.vat = parent.vat
  }

  setEan(ean): void {
    // SHAME: this is small hack to ensure translation of the last character, untranslated by directive..
    this.product.ean = BarcodeUtil.translateBarcode(ean)
  }

  getProductDepots() {
    return this.product.productDepots.filter(depot => depot.quantityStock > 0)
  }

  fillLastUsedValues(): void {
    if (this.product.productDepots && this.product.productDepots.length > 0) {
      const depotsSorted = [...this.product.productDepots].filter(d => d.depot.id === Number(this.depotTo))
      depotsSorted.sort((a, b) => {
        if (a.id > b.id) {
          return -1
        } else if (a.id < b.id) {
          return 1
        }
        return 0
      })
      if (!this.purchasePrice && depotsSorted[0] && depotsSorted[0].purchasePrice) {
        this.purchasePrice = Number(depotsSorted[0].purchasePrice.toFixed(3))
      }
      if (this.product.purchasePrice !== null && this.product.purchasePrice !== 0) {
        this.purchasePrice = Number(this.product.purchasePrice.toFixed(3))
        if (this.product.purchaseCurrency) {
          this.selectedCurrency = this.product.purchaseCurrency
        }
      }
      if (!this.selectedSupplier && depotsSorted[0] && depotsSorted[0].supplier) {
        this.selectedSupplier = depotsSorted[0].supplier.id
      }
      if (!this.quantity && !this.isQuantityLocked && depotsSorted[0] && depotsSorted[0].quantityStock) {
        this.quantity = Number(depotsSorted[0].quantityStock.toFixed(0))
      }
      if (!this.position1 && depotsSorted[0] && depotsSorted[0].position1) {
        this.position1 = depotsSorted[0].position1
      }
      if (!this.position2 && depotsSorted[0] && depotsSorted[0].position2) {
        this.position2 = depotsSorted[0].position2
      }
      if (!this.position3 && depotsSorted[0] && depotsSorted[0].position3) {
        this.position3 = depotsSorted[0].position3
      }
    } else {
      // never used product
      this.purchasePrice = 0
      if (
        !isNaN(this.product.purchasePrice) &&
        this.product.purchasePrice !== undefined &&
        this.product.purchasePrice !== null &&
        this.product.purchasePrice !== 0
      ) {
        this.purchasePrice = Number(this.product.purchasePrice.toFixed(3))
        if (this.product.purchaseCurrency) {
          this.selectedCurrency = this.product.purchaseCurrency
        }
      }
      if (this.suppliers.length === 1) {
        this.selectedSupplier = this.suppliers[0].id
      }
    }
    if (this.operationToUpdate) {
      if (this.operationToUpdate.purchaseCurrency) {
        this.selectedCurrency = this.currencies.find(c => c.id === this.operationToUpdate.purchaseCurrency)
      }
      if (this.operationToUpdate.purchaseCurrencyDate) {
        this.dateDUZP = this.operationToUpdate.purchaseCurrencyDate
      }
    }
    if (this.type === 'transfer') {
      this.purchasePrice = this.productDepot.purchasePrice
    }
  }

  amountToUnloadChanged(event, productDepot) {
    productDepot.amountToUnload = event
    this.quantity = this.productDepots.reduce((total, pd) => {
      return Number(pd.amountToUnload) + Number(total)
    }, 0)
  }

  toggleLockQuantity(): void {
    this.isQuantityLocked = !this.isQuantityLocked
    this.setViewSettings()
  }

  isProductDepotExpired(pDepot) {
    const newDate = new Date()
    const todayDate = newDate.getTime()
    if (pDepot.expirationDate?.length > 0) {
      const expDate = new Date(pDepot.expirationDate)
      return expDate.getTime() < todayDate
    } else {
      return false
    }
  }

  protected readonly event = event
}
